import {
  ComponentPropsWithoutRef,
  FunctionComponent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconProps } from '@components/icons';
import { IconButton as IconButtonGlyph } from '@components/icons/button';
import { IconDropdown } from '@components/icons/dropdown';
import { IconFormatText } from '@components/icons/format-text';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { PopoverButton } from '@components/popover-button';
import { Stack } from '@components/stack';
import { TabButton, TabButtonOption } from '@components/tab-button';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { DEFAULT_LINK_LABEL, MAX_LINK_LABEL_LENGTH } from './field-mapping';
import type { FormLinkDraft, FormLinkType } from './field-mapping';

// The pill variants LinkTypeDropdown's trigger can render as — a subset of
// helpers.tsx's VARIANT_MAP keys (which aren't a literal union there, since
// VARIANT_MAP is typed as Record<string, VariantAttributes>).
export type PillVariant = 'fieldMapped' | 'fieldMappedError';

// Matches the real props of the trigger components this dropdown is used with
// (FormatTrigger in helpers.tsx, a plain styled.button in tests) — a native
// button plus the $variant styling hook. $variant is typed as string, not
// PillVariant, because FormatTrigger shares its trigger with format-dropdown's
// full VARIANT_MAP, whose keys aren't a literal union (see the comment above).
type LinkTypeTriggerProps = ComponentPropsWithoutRef<'button'> & {
  $variant?: string;
  'data-testid'?: string;
};

export type LinkTypeDropdownProps = {
  linkType: FormLinkType;
  linkLabel: string;
  variableName: string;
  onCommit(draft: FormLinkDraft): void;
  onOpen?(): void;
  onDraftChange?(draft: FormLinkDraft | null): void;
  trigger: FunctionComponent<LinkTypeTriggerProps>;
  variant?: PillVariant;
};

// Mirrors the contentColor a given pill variant renders with in helpers.tsx's
// VARIANT_MAP (not exported to avoid a helpers.tsx <-> addons runtime cycle —
// see the type-only import note on FormatDropdown's helpers.tsx import).
const TRIGGER_VARIANT_COLOR: Record<string, Color> = {
  fieldMapped: 'elementBlue100',
  fieldMappedError: 'elementRed100',
};

type LinkTypeOption = {
  type: FormLinkType;
  testId: string;
  icon: ReactElement<IconProps>;
  descriptionKey: 'styledCta' | 'inlineLink' | 'plainUrl';
};

const LINK_TYPE_OPTIONS: LinkTypeOption[] = [
  {
    type: 'button',
    testId: 'link-type-option-button',
    icon: <IconButtonGlyph />,
    descriptionKey: 'styledCta',
  },
  {
    type: 'text',
    testId: 'link-type-option-text',
    icon: <IconFormatText />,
    descriptionKey: 'inlineLink',
  },
  {
    type: 'url',
    testId: 'link-type-option-url',
    icon: <IconTextFormatLink />,
    descriptionKey: 'plainUrl',
  },
];

export const LinkTypeDropdown = ({
  linkType,
  linkLabel,
  variableName,
  onCommit,
  onOpen,
  onDraftChange,
  trigger,
  variant,
}: LinkTypeDropdownProps) => {
  const { t } = useScopedTranslate(locale);
  const typeDisplayLabel = t(linkType);
  const variantColor = TRIGGER_VARIANT_COLOR[variant ?? 'fieldMapped'] ?? 'elementBlue100';

  return (
    <PopoverButton
      trigger={trigger}
      placement="bottom-start"
      contentStyle={{ minWidth: 'fit-content' }}
      popoverStyle={{ display: 'inline', minWidth: 0 }}
      triggerProps={{
        $variant: variant,
        'data-testid': 'form-link-type-pill',
        children: (
          <>
            <Text text={typeDisplayLabel} font="semibold12" color={variantColor} />
            <IconDropdown size={16} color={variantColor} />
          </>
        ),
      }}
    >
      {({ close }) => (
        <PopoverContent
          linkType={linkType}
          linkLabel={linkLabel}
          variableName={variableName}
          onCommit={onCommit}
          onOpen={onOpen}
          onDraftChange={onDraftChange}
          close={close}
        />
      )}
    </PopoverButton>
  );
};

type PopoverContentProps = Pick<
  LinkTypeDropdownProps,
  'linkType' | 'linkLabel' | 'variableName' | 'onCommit' | 'onOpen' | 'onDraftChange'
> & {
  close(): void;
};

// A dedicated component (rather than an inline arrow function) so its hooks are
// scoped to this popover's own mount/unmount lifecycle: PopoverButton's panel
// unmounts its render-prop content whenever the popover closes (headlessui's
// default RenderStrategy), so mount = open and unmount = closed-for-any-reason,
// which is exactly what the "outside-dismiss reverts the draft" behavior needs.
const PopoverContent = ({
  linkType,
  linkLabel,
  variableName,
  onCommit,
  onOpen,
  onDraftChange,
  close,
}: PopoverContentProps) => {
  const { t } = useScopedTranslate(locale);
  const [draft, setDraft] = useState<FormLinkDraft>({ variableName, linkType, linkLabel });
  // Set by Done/Cancel so the unmount cleanup below doesn't double-fire
  // onDraftChange(null) after an explicit commit/revert already did.
  const hasResolvedRef = useRef(false);

  useEffect(() => {
    onOpen?.();
    onDraftChange?.({ variableName, linkType, linkLabel });
    return () => {
      if (!hasResolvedRef.current) {
        onDraftChange?.(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDraft = (next: FormLinkDraft) => {
    setDraft(next);
    onDraftChange?.(next);
  };

  const selectType = (nextType: FormLinkType) => {
    const isTypingType = nextType === 'button' || nextType === 'text';
    const nextLabel =
      isTypingType && !draft.linkLabel.trim() ? DEFAULT_LINK_LABEL : draft.linkLabel;
    updateDraft({ ...draft, linkType: nextType, linkLabel: nextLabel });
  };

  const changeLabel = (value: string) => {
    updateDraft({ ...draft, linkLabel: value });
  };

  const trimmedDraftLabel = draft.linkLabel.trim() || DEFAULT_LINK_LABEL;
  // Committing identical values still rewrites the node's attributes, which
  // marks the whole form dirty — so a Done press with no changes must not
  // commit, and (matching the sidebar's Save) the button is disabled until the
  // draft differs. Both sides go through the same empty-label fallback so a
  // legacy pill (no label stored) does not read as a change either.
  const committedLabel = linkLabel.trim() || DEFAULT_LINK_LABEL;
  const isUnchanged = draft.linkType === linkType && trimmedDraftLabel === committedLabel;

  const handleDone = () => {
    hasResolvedRef.current = true;
    onCommit({
      variableName: draft.variableName,
      linkType: draft.linkType,
      linkLabel: trimmedDraftLabel,
    });
    onDraftChange?.(null);
    close();
  };

  const handleCancel = () => {
    hasResolvedRef.current = true;
    onDraftChange?.(null);
    close();
  };

  const linkTypeOptions: TabButtonOption<FormLinkType>[] = LINK_TYPE_OPTIONS.map(
    ({ type, testId, icon, descriptionKey }) => ({
      value: type,
      label: t(type),
      description: t(descriptionKey),
      icon,
      dataTestid: testId,
    })
  );

  const showLabelInput = draft.linkType === 'button' || draft.linkType === 'text';
  const previewLabel = draft.linkLabel.trim() || DEFAULT_LINK_LABEL;

  return (
    <Container>
      <TabButton
        variant="stacked"
        fullWidth
        options={linkTypeOptions}
        value={draft.linkType}
        onChange={selectType}
      />

      {/* Both branches render base-height row + preview row inside the same
          card, so the popover keeps one height across all three types — a
          size change on type switch made headlessui re-flip the panel between
          above and below the pill. */}
      {showLabelInput ? (
        <LabelGroup space={3}>
          <TextField
            label={t('label')}
            hideLabel
            dataTestid="form-link-label-input"
            value={draft.linkLabel}
            onChange={changeLabel}
            placeholder={t('labelPlaceholder')}
            maxChars={MAX_LINK_LABEL_LENGTH}
          />
          <PreviewRow data-testid="form-link-inline-preview">
            {draft.linkType === 'button' ? (
              <ButtonPreview>{previewLabel}</ButtonPreview>
            ) : (
              <TextPreview>
                <UnderlinedText text={previewLabel} font="regular14" color="elementBlue100" />
              </TextPreview>
            )}
            <Text text={t('preview')} font="regular12" color="textHelper" />
          </PreviewRow>
        </LabelGroup>
      ) : (
        <LabelGroup space={3}>
          <UrlHelperRow>
            <Text font="regular14" color="textSecondary" text={t('urlHelper')} />
          </UrlHelperRow>
          <PreviewRow data-testid="form-link-url-example">
            <UrlExampleText>{EXAMPLE_FORM_URL}</UrlExampleText>
            <Text text={t('example')} font="regular12" color="textHelper" />
          </PreviewRow>
        </LabelGroup>
      )}

      <Stack orientation="horizontal" space={2} justify="flex-end">
        <Button variant="secondary" text={t('cancel')} onClick={handleCancel} />
        <Button
          variant="primary-blue"
          text={t('done')}
          dataTestid="form-link-done"
          isDisabled={isUnchanged}
          onClick={handleDone}
        />
      </Stack>
    </Container>
  );
};

// The rendered CTA is 32px tall in the design, and the Text variant's preview
// keeps the same row height so switching type doesn't resize the block.
const PREVIEW_BUTTON_HEIGHT_PX = 32;
const PREVIEW_ROW_MIN_HEIGHT_PX = 56;

// Illustrative only (the row is captioned "Example") — the real per-traveller
// URL doesn't exist yet, and the actual forms host is app config the shared
// core package can't reach.
const EXAMPLE_FORM_URL = 'https://forms.50skills.app/...';

const Container = styled(Stack).attrs({ space: 4 })({
  width: pxToRem(340),
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(12),
  padding: pxToRem(16),
});

const LabelGroup = styled(Stack)({
  backgroundColor: theme.color.backgroundPrimary,
  borderRadius: pxToRem(12),
  padding: pxToRem(12),
  // The design sets the label field as a white card on the grey group; the
  // field's own surface matches the group, so it would otherwise disappear.
  // The card goes on the field's wrapper rather than the <input>: the wrapper
  // carries the horizontal padding, so styling the input alone left it inset
  // from the preview row below it.
  'div:has(> input)': {
    backgroundColor: theme.color.backgroundLightDark,
    border: `${pxToRem(1)} solid ${theme.color.border10}`,
    borderRadius: pxToRem(8),
  },
});

const PreviewRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: pxToRem(8),
  minHeight: pxToRem(PREVIEW_ROW_MIN_HEIGHT_PX),
  backgroundColor: theme.color.indigoTrans5,
  borderRadius: pxToRem(8),
  padding: pxToRem(12),
});

const ButtonPreview = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  height: pxToRem(PREVIEW_BUTTON_HEIGHT_PX),
  // elementBlue100, not buttonPrimary: this swatch previews the CTA the backend
  // renders into the email (#005fff in link_rendering.py), and buttonPrimary is
  // the dark navy app button — the preview would show a different colour from
  // the thing it is previewing.
  backgroundColor: theme.color.elementBlue100,
  color: theme.color.white100,
  padding: `0 ${pxToRem(16)}`,
  borderRadius: pxToRem(8),
  font: theme.font.semibold14,
});

const TextPreview = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  height: pxToRem(PREVIEW_BUTTON_HEIGHT_PX),
});

const UnderlinedText = styled(Text)({
  textDecoration: 'underline',
});

// Matches the TextField row's height so the URL card equals the label card.
const UrlHelperRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  minHeight: theme.base.height,
});

const UrlExampleText = styled.span({
  color: theme.color.elementBlue100,
  font: theme.font.regular14,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
});

const locale = {
  en: {
    button: 'Button',
    text: 'Text',
    url: 'URL',
    styledCta: 'Styled CTA',
    inlineLink: 'Inline link',
    plainUrl: 'Plain URL',
    urlHelper: 'The full form URL will be inserted as a clickable link',
    preview: 'Preview',
    example: 'Example',
    cancel: 'Cancel',
    done: 'Done',
    label: 'Link label',
    labelPlaceholder: 'Open form',
  },
};
